import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { PaymentPromiseOrigin, PaymentPromiseRecord, PaymentPromiseStatus } from '@workspace/types';
import { contactDisplayName } from '@workspace/utils';
import { contactAgentApi } from '../api/contactAgentApi';
import { contactAgentError } from '../api/contactAgentScope';
import { formatCampaignMoney } from '../components/ContactAgentMetricCard.utils';
import { ContactAgentPage, ContactAgentPageHeader } from '../components/ContactAgentPageHeader';
import { ContactAgentTenantPicker } from '../components/ContactAgentTenantPicker';
import { useContactAgentTenant } from '../hooks/useContactAgentTenant';

const PAGE_SIZE = 50;

type OriginFilter = 'all' | 'self' | 'campaign';
type StatusFilter = 'all' | PaymentPromiseStatus;

function contactLabel(item: PaymentPromiseRecord): string {
  return contactDisplayName(item.contact) || 'Contacto';
}

function formatDate(value: string): string {
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleDateString('es-AR');
}

function formatDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' });
}

const STATUS_LABEL: Record<PaymentPromiseStatus, string> = {
  ACTIVE: 'Activa',
  KEPT: 'Cumplida',
  EXPIRED: 'Vencida',
  REPLACED: 'Reemplazada',
};

function statusVariant(
  status: PaymentPromiseStatus,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (status === 'REPLACED') {
    return 'outline';
  }
  if (status === 'KEPT') {
    return 'secondary';
  }
  if (status === 'EXPIRED') {
    return 'destructive';
  }
  return 'default';
}

function originLabel(origin: PaymentPromiseOrigin, campaignName: string | null): string {
  if (origin === 'CAMPAIGN') {
    return campaignName ? `Campaña: ${campaignName}` : 'Campaña';
  }
  return 'Autogestión';
}

export function ContactAgentPaymentPromisesPage() {
  const { tenantId, tenants, isSuperAdmin, ready, selectTenant } = useContactAgentTenant();
  const [items, setItems] = useState<PaymentPromiseRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  // Por defecto se muestra todo: ocultar las vencidas escondía justo lo que hay que gestionar.
  const [originFilter, setOriginFilter] = useState<OriginFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!ready) {
      return;
    }
    let cancelled = false;
    setError('');
    void contactAgentApi
      .listPaymentPromises({
        limit: PAGE_SIZE,
        offset,
        origin: originFilter,
        ...(statusFilter === 'all' ? {} : { status: statusFilter }),
      })
      .then(result => {
        if (!cancelled) {
          setItems(result.items);
          setTotal(result.total);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(contactAgentError(err));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [ready, tenantId, offset, originFilter, statusFilter]);

  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const setOrigin = (value: OriginFilter) => {
    setOriginFilter(value);
    setOffset(0);
  };

  const setStatus = (value: StatusFilter) => {
    setStatusFilter(value);
    setOffset(0);
  };

  return (
    <ContactAgentPage>
      <ContactAgentPageHeader
        title='Compromisos de pago'
        description='Compromisos registrados por el bot, con o sin campaña. Incluye autogestión cuando el cliente escribe por web o WhatsApp.'
        toolbar={
          isSuperAdmin ? (
            <ContactAgentTenantPicker tenants={tenants} tenantId={tenantId} onChange={selectTenant} />
          ) : null
        }
      />

      {error ? <p className='text-destructive'>{error}</p> : null}

      <Card className='rounded-2xl shadow-sm'>
        <CardHeader className='space-y-4'>
          <CardTitle className='text-lg'>Listado</CardTitle>
          <div className='flex flex-wrap items-center gap-2'>
            <span className='text-sm text-muted-foreground'>Origen</span>
            <Button
              type='button'
              size='sm'
              variant={originFilter === 'self' ? 'secondary' : 'outline'}
              onClick={() => setOrigin('self')}
            >
              Autogestión
            </Button>
            <Button
              type='button'
              size='sm'
              variant={originFilter === 'campaign' ? 'secondary' : 'outline'}
              onClick={() => setOrigin('campaign')}
            >
              Campaña
            </Button>
            <Button
              type='button'
              size='sm'
              variant={originFilter === 'all' ? 'secondary' : 'outline'}
              onClick={() => setOrigin('all')}
            >
              Todas
            </Button>
          </div>
          <div className='flex flex-wrap items-center gap-2'>
            <span className='text-sm text-muted-foreground'>Estado</span>
            <Button
              type='button'
              size='sm'
              variant={statusFilter === 'ACTIVE' ? 'secondary' : 'outline'}
              onClick={() => setStatus('ACTIVE')}
            >
              Activas
            </Button>
            <Button
              type='button'
              size='sm'
              variant={statusFilter === 'KEPT' ? 'secondary' : 'outline'}
              onClick={() => setStatus('KEPT')}
            >
              Cumplidas
            </Button>
            <Button
              type='button'
              size='sm'
              variant={statusFilter === 'EXPIRED' ? 'secondary' : 'outline'}
              onClick={() => setStatus('EXPIRED')}
            >
              Vencidas
            </Button>
            <Button
              type='button'
              size='sm'
              variant={statusFilter === 'all' ? 'secondary' : 'outline'}
              onClick={() => setStatus('all')}
            >
              Todas
            </Button>
          </div>
        </CardHeader>
        <CardContent className='space-y-3'>
          {items.length === 0 ? (
            <div className='rounded-xl border border-dashed px-4 py-8 text-center text-sm text-muted-foreground'>
              No hay compromisos con estos filtros.
            </div>
          ) : (
            items.map(item => (
              <div
                key={item.id}
                className='grid gap-3 rounded-xl border px-4 py-3 md:grid-cols-[1.4fr_1fr_1fr_auto] md:items-center'
              >
                <div>
                  <Link
                    to={`/admin/contact-agent/contacts/${item.contactId}`}
                    className='font-medium text-primary hover:underline'
                  >
                    {contactLabel(item)}
                  </Link>
                  <p className='text-sm text-muted-foreground'>{item.contact.phoneE164}</p>
                  <p className='text-sm text-muted-foreground'>{item.invoiceNumber}</p>
                </div>
                <div>
                  <p className='font-medium'>{formatCampaignMoney(item.amount)}</p>
                  <p className='text-sm text-muted-foreground'>Vence {formatDate(item.promisedDate)}</p>
                </div>
                <div className='text-sm text-muted-foreground'>
                  <p>{originLabel(item.origin, item.campaignName)}</p>
                  <p>Registrada {formatDateTime(item.createdAt)}</p>
                  {item.channel ? <p>Canal {item.channel}</p> : null}
                </div>
                <Badge variant={statusVariant(item.status)}>{STATUS_LABEL[item.status]}</Badge>
              </div>
            ))
          )}

          {total > PAGE_SIZE ? (
            <div className='flex items-center justify-between pt-2'>
              <p className='text-sm text-muted-foreground'>
                Página {page} de {totalPages} · {total} compromisos
              </p>
              <div className='flex gap-2'>
                <Button
                  type='button'
                  size='sm'
                  variant='outline'
                  disabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                >
                  Anterior
                </Button>
                <Button
                  type='button'
                  size='sm'
                  variant='outline'
                  disabled={offset + PAGE_SIZE >= total}
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </ContactAgentPage>
  );
}
